import type { GuestAccessRuleDto } from '../../api/guestAccessTypes';
import { isoToDatePart, isoToTimeHHMM } from '../events/iso';

export type GuestAccessGroup = {
  title: string;
  resourceIds: string[];
};

/** Tables an admin can open for guests, grouped by room. */
export const GUEST_ACCESS_GROUPS: GuestAccessGroup[] = [
  { title: 'Таверна', resourceIds: ['table_game_1', 'table_game_2', 'table_game_3'] },
  { title: 'Двор', resourceIds: ['table_game_4'] },
  { title: 'VIP-комната', resourceIds: ['table_game_5', 'table_game_6', 'table_game_7'] },
];

const VIP_HALVES = ['table_game_5', 'table_game_6'];
const VIP_COMBINED = 'table_game_7';

/** The joined VIP table excludes both VIP halves and vice versa. */
export function isBlockedByVipExclusion(resourceId: string, selected: ReadonlySet<string>): boolean {
  if (resourceId === VIP_COMBINED) return VIP_HALVES.some((id) => selected.has(id));
  if (VIP_HALVES.includes(resourceId)) return selected.has(VIP_COMBINED);
  return false;
}

const WEEKDAYS = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
const MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/** "Пятница (31 октября)" */
export function formatRuleDay(startAt: string): string {
  const day = isoToDatePart(startAt);
  if (!day) return startAt;
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAYS[date.getDay()]} (${d} ${MONTHS[m - 1]})`;
}

export function formatRuleTime(rule: Pick<GuestAccessRuleDto, 'startAt' | 'endAt'>): string {
  return `${isoToTimeHHMM(rule.startAt) ?? '??'}–${isoToTimeHHMM(rule.endAt) ?? '??'}`;
}

/** Club-local wall-clock timestamp for query strings and request bodies. */
export function toLocalDateTime(dateYYYYMMDD: string, timeHHMM: string): string {
  return `${dateYYYYMMDD}T${timeHHMM}:00`;
}

export function isRuleFinished(rule: Pick<GuestAccessRuleDto, 'endAt'>, now: Date = new Date()): boolean {
  const day = isoToDatePart(rule.endAt);
  const time = isoToTimeHHMM(rule.endAt);
  if (!day || !time) return false;
  const [y, m, d] = day.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm).getTime() <= now.getTime();
}

/** Rules that touch the calendar day `dateKey` (YYYY-MM-DD) for the table. */
export function rulesForResourceOnDate(
  rules: readonly GuestAccessRuleDto[],
  resourceId: string,
  dateKey: string,
): GuestAccessRuleDto[] {
  return rules.filter((rule) => {
    if (!rule.resourceIds.includes(resourceId)) return false;
    const startDay = isoToDatePart(rule.startAt);
    const endDay = isoToDatePart(rule.endAt);
    return startDay != null && endDay != null && startDay <= dateKey && dateKey <= endDay;
  });
}

/** The earliest-starting rule that touches the calendar day `dateKey` (YYYY-MM-DD), if any. */
export function nearestRuleOnDate(rules: readonly GuestAccessRuleDto[], dateKey: string): GuestAccessRuleDto | null {
  let nearest: GuestAccessRuleDto | null = null;
  for (const rule of rules) {
    const startDay = isoToDatePart(rule.startAt);
    const endDay = isoToDatePart(rule.endAt);
    if (startDay == null || endDay == null || startDay > dateKey || dateKey > endDay) continue;
    if (nearest == null || rule.startAt < nearest.startAt) nearest = rule;
  }
  return nearest;
}

export function openResourceIdsOnDate(rules: readonly GuestAccessRuleDto[], dateKey: string): Set<string> {
  const open = new Set<string>();
  for (const rule of rules) {
    for (const resourceId of rule.resourceIds) {
      if (rulesForResourceOnDate([rule], resourceId, dateKey).length > 0) open.add(resourceId);
    }
  }
  return open;
}
